/**
 * VirtTopologyCanvas — Diagrama interactivo de la infraestructura virtual
 * Muestra clusters → hypervisores → VMs + vSwitches en un canvas SVG con pan y zoom
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ── Colors ──────────────────────────────────────────────────────────────────
const C = {
  cluster:   { fill: 'rgba(139,92,246,.12)',   stroke: 'rgba(139,92,246,.8)',  text: '#c4b5fd' },
  hyp:       { fill: 'rgba(59,130,246,.12)',    stroke: 'rgba(59,130,246,.7)',  text: '#93c5fd' },
  vm_on:     { fill: 'rgba(16,185,129,.12)',    stroke: '#4ade80',              text: '#6ee7b7' },
  vm_off:    { fill: 'rgba(239,68,68,.12)',     stroke: '#f87171',              text: '#fca5a5' },
  vm_susp:   { fill: 'rgba(234,179,8,.12)',     stroke: '#fbbf24',              text: '#fde68a' },
  vm_unk:    { fill: 'var(--bg-input)',   stroke: 'var(--slate-300)', text: 'var(--text-muted)' },
  vswitch:   { fill: 'rgba(52,211,153,.12)',    stroke: '#34d399',              text: '#6ee7b7' },
  standalone:{ fill: 'rgba(56,189,248,.10)',    stroke: 'rgba(56,189,248,.6)',  text: '#7dd3fc' },
};

const HYP_ICON = { vmware: '🟦', proxmox: '🟧', hyper_v: '🪟', kvm: '🐧', xen: '🌐', nutanix: '🔵', other: '⚙️' };
const VM_TYPE_ICON = { windows: '🪟', linux: '🐧', other: '☁️' };

function vmColor(state) {
  if (state === 'on')        return C.vm_on;
  if (state === 'off')       return C.vm_off;
  if (state === 'suspended') return C.vm_susp;
  return C.vm_unk;
}

// Node dimensions
const HYP_W = 170, HYP_H = 56;
const VM_W  = 130, VM_H  = 44;
const VS_W  = 150, VS_H  = 44;
const CLUSTER_PAD = 20;
const COL_GAP     = 28;   // horizontal gap between hypervisors inside a cluster
const ROW_GAP     = 28;   // vertical gap: hyp → VMs
const VM_GAP      = 8;    // gap between VMs
const VS_GAP      = 12;   // gap: last-VM → first-vSwitch
const CLUSTER_GAP = 48;   // gap between clusters
const MIN_CLUSTER_W     = 190;
const CLUSTER_HEADER_H  = 38;

// ─────────────────────────────────────────────────────────────────────────────
//  Layout builder
// ─────────────────────────────────────────────────────────────────────────────
function buildLayout(hypervisors, clusters, vms, vswitches) {
  // Group VMs by hypervisor
  const vmsByHyp = {};
  vms.forEach(v => {
    const key = String(v.hypervisor_id || '_none');
    if (!vmsByHyp[key]) vmsByHyp[key] = [];
    vmsByHyp[key].push(v);
  });

  // Group vSwitches by hypervisor (prefer hypervisor_id, fallback cluster-level)
  const vsByHyp     = {};  // hypId  → [vswitch]
  const vsByCluster = {};  // clustId → [vswitch]  (distributed / no hyp)
  const vsOrphan    = [];  // neither
  vswitches.forEach(vs => {
    if (vs.hypervisor_id) {
      const k = String(vs.hypervisor_id);
      if (!vsByHyp[k]) vsByHyp[k] = [];
      vsByHyp[k].push(vs);
    } else if (vs.cluster_id) {
      const k = String(vs.cluster_id);
      if (!vsByCluster[k]) vsByCluster[k] = [];
      vsByCluster[k].push(vs);
    } else {
      vsOrphan.push(vs);
    }
  });

  // Build hypId→clusterId map from cluster.nodes (N:M membership table)
  const hypClusterMap = {}; // hypId -> clusterId (string)
  clusters.forEach(c => {
    (c.nodes || []).forEach(n => {
      hypClusterMap[String(n.hypervisor_id)] = String(c.id);
    });
  });

  // Group hypervisors by cluster using the membership map
  const hypsByCluster = {};
  hypervisors.forEach(h => {
    const key = hypClusterMap[String(h.id)] || '_standalone';
    if (!hypsByCluster[key]) hypsByCluster[key] = [];
    hypsByCluster[key].push(h);
  });

  // All section blocks (clusters + standalone)
  const sections = [];

  // Standalone hypervisors first
  const standalone = hypsByCluster['_standalone'] || [];
  if (standalone.length) {
    sections.push({ type: 'standalone', hypervisors: standalone });
  }

  // Clusters
  clusters.forEach(c => {
    const hyps = hypsByCluster[String(c.id)] || [];
    sections.push({ type: 'cluster', cluster: c, hypervisors: hyps });
  });

  // Compute width of each hypervisor column (HYP_W, plus its VMs)
  // Each hypervisor gets a column with VMs stacked below it
  const nodes = [];
  const edges = [];

  let curX = 60;
  const HYP_Y = 80;
  const VM_Y  = HYP_Y + HYP_H + ROW_GAP;

  // helper: column height for a hypervisor (hyp box + vms + vswitches)
  function colHeight(h) {
    const vmCount = (vmsByHyp[String(h.id)] || []).length;
    const vsCount = (vsByHyp[String(h.id)] || []).length;
    return HYP_H + ROW_GAP
      + (vmCount > 0 ? vmCount * (VM_H + VM_GAP) : 0)
      + (vsCount > 0 ? (vmCount > 0 ? VS_GAP : ROW_GAP) + vsCount * (VS_H + VM_GAP) : 0);
  }

  // Hypervisors inside cluster start below the header band
  const HYP_Y_IN_CLUSTER = HYP_Y + CLUSTER_HEADER_H;

  for (const section of sections) {
    const sectionStartX = curX;
    const hypTop = section.type === 'cluster' ? HYP_Y_IN_CLUSTER : HYP_Y;
    const vmTop  = hypTop + HYP_H + ROW_GAP;

    for (const h of section.hypervisors) {
      const hx = curX;
      const hy = hypTop;

      nodes.push({ id: `hyp-${h.id}`, type: 'hypervisor', data: h, x: hx, y: hy, w: HYP_W, h: HYP_H });

      // VMs below hypervisor
      const hyVms = vmsByHyp[String(h.id)] || [];
      let nextY = vmTop;
      hyVms.forEach(vm => {
        const vmx = hx + (HYP_W - VM_W) / 2;
        nodes.push({ id: `vm-${vm.id}`, type: 'vm', data: vm, x: vmx, y: nextY, w: VM_W, h: VM_H });
        edges.push({ from: `hyp-${h.id}`, to: `vm-${vm.id}`, type: 'hyp-vm', state: vm.power_state });
        nextY += VM_H + VM_GAP;
      });

      // vSwitches below VMs (or directly below hyp if no VMs)
      const hyVSwitches = vsByHyp[String(h.id)] || [];
      if (hyVSwitches.length > 0) {
        nextY += hyVms.length > 0 ? VS_GAP : ROW_GAP;
        hyVSwitches.forEach(vs => {
          const vsx = hx + (HYP_W - VS_W) / 2;
          nodes.push({ id: `vs-${vs.id}`, type: 'vswitch', data: vs, x: vsx, y: nextY, w: VS_W, h: VS_H });
          edges.push({ from: `hyp-${h.id}`, to: `vs-${vs.id}`, type: 'hyp-vs' });
          nextY += VS_H + VM_GAP;
        });
      }

      curX += Math.max(HYP_W, VM_W, VS_W) + COL_GAP;
    }

    // Cluster-level vSwitches (distributed — no hypervisor_id)
    const clusterVSwitches = section.type === 'cluster'
      ? (vsByCluster[String(section.cluster.id)] || [])
      : [];

    // After iterating hypervisors, compute section right edge
    const hasHyps = section.hypervisors.length > 0;
    const contentW = hasHyps ? (curX - COL_GAP - sectionStartX) : 0;
    const boxW = Math.max(contentW + CLUSTER_PAD * 2, MIN_CLUSTER_W);

    // If cluster/standalone was empty, advance curX
    if (!hasHyps) {
      curX = sectionStartX + boxW - CLUSTER_PAD;
    }

    if (section.type === 'cluster') {
      const maxColH = hasHyps
        ? Math.max(...section.hypervisors.map(h => colHeight(h)))
        : 0;
      // Extra row for cluster-level vSwitches at the bottom
      const clVsH = clusterVSwitches.length > 0
        ? VS_GAP + clusterVSwitches.length * (VS_H + VM_GAP)
        : 0;
      const clusterH = CLUSTER_HEADER_H + CLUSTER_PAD
        + (maxColH > 0 ? maxColH : HYP_H + ROW_GAP + VM_H)
        + clVsH + CLUSTER_PAD;

      const clNode = {
        id: `cluster-${section.cluster.id}`,
        type: 'cluster',
        data: section.cluster,
        x: sectionStartX - CLUSTER_PAD,
        y: HYP_Y - CLUSTER_PAD,
        w: boxW,
        h: clusterH,
      };
      nodes.push(clNode);

      // Place cluster-level vSwitches centred inside the cluster box
      if (clusterVSwitches.length > 0) {
        const clBaseY = (clNode.y + clNode.h) - CLUSTER_PAD - clusterVSwitches.length * (VS_H + VM_GAP);
        let vsX = clNode.x + CLUSTER_PAD;
        clusterVSwitches.forEach(vs => {
          nodes.push({ id: `vs-${vs.id}`, type: 'vswitch', data: vs, x: vsX, y: clBaseY, w: VS_W, h: VS_H });
          edges.push({ from: `cluster-${section.cluster.id}`, to: `vs-${vs.id}`, type: 'cluster-vs' });
          vsX += VS_W + COL_GAP;
        });
      }
    } else if (section.type === 'standalone') {
      const maxColH = hasHyps
        ? Math.max(...section.hypervisors.map(h => colHeight(h)))
        : 0;
      const standaloneH = CLUSTER_PAD
        + (maxColH > 0 ? maxColH : HYP_H + ROW_GAP + VM_H)
        + CLUSTER_PAD;
      nodes.push({
        id: 'standalone-section',
        type: 'standalone',
        x: sectionStartX - CLUSTER_PAD,
        y: HYP_Y - CLUSTER_PAD,
        w: boxW,
        h: standaloneH,
      });
    }

    curX += CLUSTER_GAP;
  }

  // Truly orphan vSwitches (no hypervisor_id, no cluster_id) in a trailing column
  if (vsOrphan.length > 0) {
    const vsX = curX + 20;
    let vsY = HYP_Y;
    vsOrphan.forEach(vs => {
      nodes.push({ id: `vs-${vs.id}`, type: 'vswitch', data: vs, x: vsX, y: vsY, w: VS_W, h: VS_H });
      vsY += VS_H + 16;
    });
  }

  return { nodes, edges };
}

// ─────────────────────────────────────────────────────────────────────────────
//  SVG Nodes
// ─────────────────────────────────────────────────────────────────────────────
function ClusterNode({ node, selected, onClick }) {
  const col = C.cluster;
  const hh = CLUSTER_HEADER_H;
  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      {/* Outer container */}
      <rect
        x={node.x} y={node.y} width={node.w} height={node.h}
        rx={12}
        fill={col.fill}
        stroke={selected ? '#7c3aed' : col.stroke}
        strokeWidth={selected ? 2.5 : 1.5}
        strokeDasharray="6 3"
      />
      {/* Solid header band */}
      <rect
        x={node.x} y={node.y} width={node.w} height={hh}
        rx={12} fill={col.stroke} opacity={0.35}
      />
      {/* Square off header bottom corners */}
      <rect
        x={node.x} y={node.y + hh - 8} width={node.w} height={8}
        fill={col.stroke} opacity={0.35}
      />
      {/* Cluster name */}
      <text x={node.x + 10} y={node.y + 15} fontSize={12} fontWeight={700} fill={col.text}>
        🔗 {node.data.name}
      </text>
      {node.data.cluster_type && (
        <text x={node.x + 10} y={node.y + 29} fontSize={9} fill={col.text} opacity={0.85}>
          {node.data.cluster_type}
        </text>
      )}
      {/* Hypervisor count pill */}
    </g>
  );
}

function StandaloneSection({ node }) {
  return (
    <g>
      <rect
        x={node.x} y={node.y} width={node.w} height={node.h}
        rx={10}
        fill="var(--bg-toolbar)" stroke="var(--slate-300)"
        strokeWidth={1} strokeDasharray="4 3"
      />
      <text x={node.x + 10} y={node.y + 16} fontSize={10} fontWeight={600} fill="var(--text-muted)">
        🖥️ Sin cluster
      </text>
    </g>
  );
}

function HypervisorNode({ node, selected, onClick }) {
  const col = C.hyp;
  const icon = HYP_ICON[node.data.hypervisor_type] || '🖥️';
  const isActive = node.data.status === 'active';
  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={8}
        fill={col.fill} stroke={selected ? '#2563eb' : col.stroke}
        strokeWidth={selected ? 2.5 : 1.5}
      />
      {/* status dot */}
      <circle cx={node.x + node.w - 10} cy={node.y + 10} r={5}
        fill={isActive ? '#22c55e' : '#ef4444'}
      />
      <text x={node.x + 10} y={node.y + 22} fontSize={13}>{icon}</text>
      <text x={node.x + 28} y={node.y + 22} fontSize={11} fontWeight={700} fill={col.text}>
        {node.data.name.length > 16 ? node.data.name.slice(0, 15) + '…' : node.data.name}
      </text>
      <text x={node.x + 10} y={node.y + 36} fontSize={9} fill={col.text} opacity={0.65}>
        {node.data.hypervisor_type}{node.data.version ? ` v${node.data.version}` : ''}
      </text>
    </g>
  );
}

function VmNode({ node, selected, onClick }) {
  const col = vmColor(node.data.power_state);
  const icon = VM_TYPE_ICON[node.data.vm_type] || '☁️';
  const name = node.data.name.length > 16 ? node.data.name.slice(0, 15) + '…' : node.data.name;
  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={6}
        fill={col.fill} stroke={selected ? col.text : col.stroke}
        strokeWidth={selected ? 2 : 1}
      />
      <text x={node.x + 8} y={node.y + 18} fontSize={11}>{icon}</text>
      <text x={node.x + 22} y={node.y + 18} fontSize={10} fontWeight={600} fill={col.text}>{name}</text>
      <text x={node.x + 8} y={node.y + 32} fontSize={9} fill={col.text} opacity={0.7}>
        {node.data.cpu_cores ? `${node.data.cpu_cores}vCPU` : ''}{node.data.ram_gb ? ` · ${node.data.ram_gb}GB` : ''}{!node.data.cpu_cores && !node.data.ram_gb ? node.data.power_state || '' : ''}
      </text>
    </g>
  );
}

function VSwitchNode({ node, selected, onClick }) {
  const col = C.vswitch;
  return (
    <g onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={8}
        fill={col.fill} stroke={selected ? '#065f46' : col.stroke}
        strokeWidth={selected ? 2.5 : 1.5}
      />
      <text x={node.x + 8} y={node.y + 20} fontSize={13}>🔀</text>
      <text x={node.x + 26} y={node.y + 20} fontSize={11} fontWeight={700} fill={col.text}>
        {node.data.name.length > 15 ? node.data.name.slice(0, 14) + '…' : node.data.name}
      </text>
      <text x={node.x + 8} y={node.y + 36} fontSize={9} fill={col.text} opacity={0.65}>
        {node.data.vswitch_type || ''} · {(node.data.port_groups || []).length} PGs
      </text>
    </g>
  );
}

// Edge helper – center-bottom to center-top
function nodeAnchor(node, side) {
  if (side === 'bottom') return { x: node.x + node.w / 2, y: node.y + node.h };
  if (side === 'top')    return { x: node.x + node.w / 2, y: node.y };
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function Edge({ edge, nodeMap }) {
  const from = nodeMap[edge.from];
  const to   = nodeMap[edge.to];
  if (!from || !to) return null;
  const p1 = nodeAnchor(from, 'bottom');
  const p2 = nodeAnchor(to, 'top');
  const my  = (p1.y + p2.y) / 2;
  const d   = `M${p1.x},${p1.y} C${p1.x},${my} ${p2.x},${my} ${p2.x},${p2.y}`;
  const color = edge.type === 'hyp-vs'     ? '#34d399' :
                edge.type === 'cluster-vs' ? '#34d399' :
                edge.state === 'on'        ? '#4ade80' :
                edge.state === 'off'       ? '#f87171' :
                edge.state === 'suspended' ? '#fbbf24' : '#d1d5db';
  const dash = (edge.type === 'hyp-vs' || edge.type === 'cluster-vs') ? '4 3' : undefined;
  return <path d={d} fill="none" stroke={color} strokeWidth={1.5} opacity={0.7} strokeDasharray={dash} />;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tooltip
// ─────────────────────────────────────────────────────────────────────────────
function Tooltip({ node }) {
  if (!node) return null;
  const { data, type } = node;
  const lines = [];
  if (type === 'hypervisor') {
    lines.push(['Nombre',       data.name]);
    lines.push(['Tipo',         data.hypervisor_type]);
    data.version   && lines.push(['Versión',    data.version]);
    data.mgmt_ip   && lines.push(['Mgmt IP',    data.mgmt_ip]);
    lines.push(['Estado',       data.status || 'active']);
  } else if (type === 'vm') {
    lines.push(['VM',           data.name]);
    lines.push(['Estado',       data.power_state || '—']);
    data.vm_type   && lines.push(['OS',         data.vm_type]);
    data.cpu_cores && lines.push(['vCPU',       data.cpu_cores]);
    data.ram_gb    && lines.push(['vRAM',       `${data.ram_gb} GB`]);
  } else if (type === 'vswitch') {
    lines.push(['vSwitch',      data.name]);
    data.vswitch_type && lines.push(['Tipo', data.vswitch_type]);
    lines.push(['Port groups',  (data.port_groups || []).length]);
  } else if (type === 'cluster') {
    lines.push(['Cluster',      data.name]);
    data.cluster_type && lines.push(['Tipo',  data.cluster_type]);
  }
  return (
    <div style={{
        position: 'fixed', background: 'var(--bg-surface)', color: 'var(--text-primary)', borderRadius: 8,
      padding: '8px 12px', fontSize: 12, pointerEvents: 'none', zIndex: 9999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)', minWidth: 150,
      top: '50%', right: 24, transform: 'translateY(-50%)',
    }}>
      {lines.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
          <span style={{ color: 'var(--text-muted)', minWidth: 72 }}>{k}</span>
          <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main
// ─────────────────────────────────────────────────────────────────────────────
export default function VirtTopologyCanvas({ companyId }) {
  const nav = useNavigate();
  const [hypervisors, setHypervisors] = useState([]);
  const [clusters,    setClusters]    = useState([]);
  const [vms,         setVms]         = useState([]);
  const [vswitches,   setVswitches]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);

  const [vp, setVp] = useState({ x: 0, y: 0, scale: 1 });
  const dragging = useRef(null);
  const svgRef   = useRef();

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      api.getHypervisors(companyId),
      api.getClusters(companyId),
      api.getVMs(companyId),
      api.getVSwitches(companyId),
    ]).then(([h, c, v, vs]) => {
      setHypervisors(h.data || []);
      setClusters(c.data || []);
      setVms(v.data || []);
      setVswitches(vs.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [companyId]);

  const { nodes, edges } = React.useMemo(
    () => buildLayout(hypervisors, clusters, vms, vswitches),
    [hypervisors, clusters, vms, vswitches]
  );

  const nodeMap = React.useMemo(
    () => Object.fromEntries(nodes.map(n => [n.id, n])),
    [nodes]
  );

  // Bounding box for "fit to screen"
  const bbox = React.useMemo(() => {
    if (!nodes.length) return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    const xs = nodes.flatMap(n => [n.x, n.x + n.w]);
    const ys = nodes.flatMap(n => [n.y, n.y + n.h]);
    return { minX: Math.min(...xs) - 40, minY: Math.min(...ys) - 40, maxX: Math.max(...xs) + 60, maxY: Math.max(...ys) + 60 };
  }, [nodes]);

  const handleFit = useCallback(() => {
    if (!svgRef.current) return;
    const { width: W, height: H } = svgRef.current.getBoundingClientRect();
    const sceneW = bbox.maxX - bbox.minX;
    const sceneH = bbox.maxY - bbox.minY;
    const scale  = Math.min(1.4, Math.min(W / sceneW, H / sceneH) * 0.9);
    setVp({ x: W / 2 - (bbox.minX + sceneW / 2) * scale, y: H / 2 - (bbox.minY + sceneH / 2) * scale, scale });
  }, [bbox]);

  // Auto-fit on load
  useEffect(() => { if (!loading && nodes.length) { setTimeout(handleFit, 50); } }, [loading, nodes.length, handleFit]);

  const onWheel = useCallback(e => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    setVp(v => {
      const newScale = Math.max(0.2, Math.min(3, v.scale * factor));
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return v;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      return {
        scale: newScale,
        x: mx - (mx - v.x) * (newScale / v.scale),
        y: my - (my - v.y) * (newScale / v.scale),
      };
    });
  }, []);

  // El zoom va con listener NATIVO y `passive: false`: React registra `wheel`
  // como pasivo en la raíz, asi que un preventDefault dentro de onWheel avisa
  // ("Unable to preventDefault inside passive event listener invocation") y no
  // llega a evitar el scroll/zoom de la pagina.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel, loading, nodes.length]);

  const onMouseDown = useCallback(e => {
    if (e.target.closest('g')) return; // clicking a node
    e.preventDefault();
    dragging.current = { startX: e.clientX - vp.x, startY: e.clientY - vp.y };
  }, [vp]);

  const onMouseMove = useCallback(e => {
    const d = dragging.current;
    // Si el puntero sale del lienzo (onMouseLeave) o se suelta el raton fuera,
    // el ref puede ser null; se leen los valores ANTES del updater porque React
    // puede ejecutarlo mas tarde (era el crash "Cannot read properties of null
    // (reading 'startX')" al arrastrar y sacar el dibujo fuera).
    if (!d || e.buttons === 0) { dragging.current = null; return; }
    const x = e.clientX - d.startX;
    const y = e.clientY - d.startY;
    setVp(v => ({ ...v, x, y }));
  }, []);

  const stopDrag = useCallback(() => { dragging.current = null; }, []);

  const onNodeClick = useCallback(node => {
    setSelected(s => s?.id === node.id ? null : node);
  }, []);

  // Legend
  const legend = [
    { c: C.cluster.stroke,  label: 'Cluster' },
    { c: C.hyp.stroke,      label: 'Hypervisor' },
    { c: C.vm_on.stroke,    label: 'VM encendida' },
    { c: C.vm_off.stroke,   label: 'VM apagada' },
    { c: C.vm_susp.stroke,  label: 'VM suspendida' },
    { c: C.vswitch.stroke,  label: 'vSwitch' },
  ];

  if (!companyId) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Selecciona una empresa.</div>;

  return (
    <div className="vtc-page" style={{
      height: 'calc(100vh - 60px)',
      margin: '-2rem',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--slate-50)',
      overflow: 'hidden',
    }}>
      {/* Toolbar */}
      <div className="vtc-toolbar" style={{
        padding: '8px 16px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <span className="vtc-title" style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>📐 Topología de Virtualización</span>
        <div style={{ flexGrow: 1 }} />
        {/* Legend */}
        <div className="vtc-legend" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {legend.map(l => (
            <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: l.c, display: 'inline-block' }} />
              {l.label}
            </span>
          ))}
        </div>
        <button onClick={handleFit} style={btnStyle('#6366f1')}>⤢ Ajustar</button>
        <button onClick={() => setVp(v => ({ ...v, scale: Math.min(3, v.scale * 1.2) }))} style={btnStyle('#6b7280')}>+</button>
        <button onClick={() => setVp(v => ({ ...v, scale: Math.max(0.2, v.scale / 1.2) }))} style={btnStyle('#6b7280')}>−</button>
        <button onClick={() => nav('/virt/overview')} style={btnStyle('#374151', true)}>📊 Resumen</button>
      </div>

      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
          <span className="spinner-border spinner-border-sm me-2" /> Cargando topología…
        </div>
      )}

      {!loading && nodes.length === 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 48, opacity: 0.3 }}>🖥️</div>
          <div style={{ fontWeight: 600 }}>Sin infraestructura registrada</div>
          <div style={{ fontSize: 13 }}>Añade hypervisores, clusters o VMs para ver la topología</div>
        </div>
      )}

      {!loading && nodes.length > 0 && (
        <svg
          ref={svgRef}
          style={{ flex: 1, cursor: dragging.current ? 'grabbing' : 'grab', userSelect: 'none' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {/* Grid dots */}
          <defs>
            <pattern id="vgrid" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="var(--border)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#vgrid)" />

          <g transform={`translate(${vp.x},${vp.y}) scale(${vp.scale})`}>
            {/* Standalone section box */}
            {nodes.filter(n => n.type === 'standalone').map(n => (
              <StandaloneSection key={n.id} node={n} />
            ))}
            {/* Cluster backgrounds first */}
            {nodes.filter(n => n.type === 'cluster').map(n => (
              <ClusterNode key={n.id} node={n} selected={selected?.id === n.id} onClick={onNodeClick} />
            ))}
            {/* Edges */}
            {edges.map((e, i) => <Edge key={i} edge={e} nodeMap={nodeMap} />)}
            {/* Hypervisors */}
            {nodes.filter(n => n.type === 'hypervisor').map(n => (
              <HypervisorNode key={n.id} node={n} selected={selected?.id === n.id} onClick={onNodeClick} />
            ))}
            {/* VMs */}
            {nodes.filter(n => n.type === 'vm').map(n => (
              <VmNode key={n.id} node={n} selected={selected?.id === n.id} onClick={onNodeClick} />
            ))}
            {/* vSwitches (positioned inline below their hypervisor/cluster) */}
            {nodes.filter(n => n.type === 'vswitch').map(n => (
              <VSwitchNode key={n.id} node={n} selected={selected?.id === n.id} onClick={onNodeClick} />
            ))}
          </g>
        </svg>
      )}

      {selected && <Tooltip node={selected} />}
    </div>
  );
}

function btnStyle(color, outline = false) {
  return {
    padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: outline ? 'var(--bg-input)' : color,
    color: outline ? color : '#fff',
    border: `1px solid ${color}`,
  };
}
